import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { Box, Button } from "@mui/material";
import GenericFormPopup from "../GenericFormPopup/GenericFormPopup";
import { apiVariable } from "../../../api/apiVariable";
import useAuth from "../../../hooks/useAuth";
import { OptionType } from "../../../interfaces/Fleet/IGenericFormPopupProps";
import StatusSnackbar from "../../General/StatusSnackbar/StatusSnackbar";
import {
  makeFieldConfigDynamic,
  yardRepairFieldConfiguration,
  yardRepairInitialValues,
} from "../../../formConfigs/Fleet/yardRepairFieldConfig";
import FileUploadModal from "../FileUploadModal/FileUploadModal";
import dayjs from "dayjs";
import TruckPopup, { fetchOwnerCompanies } from "../TruckPopup/TruckPopup";
import { fetchAllDrivers, fetchAssets } from "../IncidentPopup/IncidentPopup";
import DriverPopup from "../DriverPopup/DriverPopup";
import TrailerPopup from "../TrailerPopup/TrailerPopup";
import { getId } from "../../HR/UpdateEmployeePopup/UpdateEmployeePopup";
import CompanyPopup from "../CompanyPopup/CompanyPopup";

const YardRepairPopup = () => {
  const [open, setOpen] = useState(false);
  const [driverData, setDriverData] = useState<OptionType[]>([]);
  const [selectedAssetType, setSelectedAssetType] = useState<string>("");
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [fileUploadModalOpen, setFileUploadModalOpen] = useState(false);
  const [yardRepairData, setYardRepairData] = useState<any>(null);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  const handleSnackbarClose = () => setStatus("idle");
  const queryClient = useQueryClient();
  const { token } = useAuth();
  const prevAssetAffected = useRef();
  const prevAssetNumber = useRef();
  const prevDriverId = useRef();

  const {
    data: assetData,
    isError,
    error,
  } = useQuery({
    queryKey: ["assets", selectedAssetType],
    queryFn: () => fetchAssets(selectedAssetType, token),
    enabled: !!selectedAssetType,
  });

  const { data: allDrivers } = useQuery({
    queryKey: ["drivers"],
    queryFn: () => fetchAllDrivers(token),
    enabled: !!token,
  });

  const { data: ownerCompanies } = useQuery({
    queryKey: ["ownerCompanies"],
    queryFn: () => fetchOwnerCompanies(token),
    enabled: !!open,
  });

  useEffect(() => {
    if (allDrivers) {
      setDriverData(allDrivers);
    }
  }, [allDrivers]);

  useEffect(() => {
    if (isError && error) {
      setStatus("error");
      setErrorMessage(
        (error as any).response?.data || "Failed to fetch assets"
      );
    }
  }, [isError, error]);

  const yardRepairMutation = useMutation({
    mutationFn: (data: any) =>
      axios
        .post(`${apiVariable}/yardRepair`, data, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })
        .then((response) => {
          if (response.data.invoiceNumber) {
            setYardRepairData(response.data);
            setFileUploadModalOpen(true);
          }
        }),
    onSuccess: () => {
      setStatus("success");
      handleClose();
      queryClient.invalidateQueries({ queryKey: ["yardRepairs"] });
    },
    onError: (error: any) => {
      setStatus("error");
      setErrorMessage(error.response.data || "Failed to submit incident");
    },
  });

  const submitHandler = (data: any) => {
    const requiredFields = [
      "assetAffected",
      "assetNumber",
      "description",
      "amount",
      "invoiceDate",
      "invoiceNumber",
      "authorizedBy",
      "ownerCompanyId",
    ];

    const missingFields = requiredFields.filter((field) => !data[field]);

    if (missingFields.length > 0) {
      setStatus("error");
      setErrorMessage("Please fill in all fields.");
      return;
    }

    const submitData = {
      assetAffected: data.assetAffected !== "" ? data.assetAffected : null,
      assetNumber: data.assetNumber !== "" ? getId(data.assetNumber) : null,
      driverId: data.driverId !== "" ? getId(data.driverId) : null,
      ownerCompanyId:
        data.ownerCompanyId !== "" ? getId(data.ownerCompanyId) : null,
      description: data.description,
      amount: data.amount,
      invoiceDate: dayjs(data.invoiceDate).format("YYYY-MM-DD"),
      invoiceNumber: data.invoiceNumber,
      authorizedBy: data.authorizedBy,
      mileage: data.mileage !== "" ? data.mileage : null,
      isCompanyCharged:
        data.isCompanyCharged !== "" ? data.isCompanyCharged : null,
      chargedCompanyAmount:
        data.chargedCompanyAmount !== "" ? data.chargedCompanyAmount : null,
      isDriverCharged:
        data.isDriverCharged !== "" ? data.isDriverCharged : null,
      chargedDriverAmount:
        data.chargedDriverAmount !== "" ? data.chargedDriverAmount : null,
    };

    yardRepairMutation.mutate(submitData);
  };
  const handleFetchAssignedDriver = async (truckId: string) => {
    try {
      const response = await axios.get(
        `${apiVariable}/getAssignedDriverOnTruck/${truckId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const assignedDriver = response.data;

      if (assignedDriver && assignedDriver.id) {
        const formattedAssignedDriver = {
          label: `${assignedDriver.firstName} ${assignedDriver.lastName} | ${assignedDriver.phoneNumber}`,
          id: assignedDriver.id.toString(),
        };
        setDriverData([formattedAssignedDriver]);
      } else {
        setDriverData(allDrivers || []);
      }
    } catch (error) {
      setErrorMessage(
        "No driver is assigned to this truck. Please select a driver manually."
      );
      setStatus("error");
      setDriverData(allDrivers || []);
    }
  };

  const handleFetchDriverFromTrailer = async (trailerId: string) => {
    try {
      const { data: truck } = await axios.get(
        `${apiVariable}/getTruckFromTrailer/${trailerId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (!truck?.id) {
        setDriverData(allDrivers || []);
        return;
      }

      const { data: assignedDriver } = await axios.get(
        `${apiVariable}/getAssignedDriverOnTruck/${truck.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (assignedDriver?.id) {
        setDriverData([
          {
            label: `${assignedDriver.firstName} ${assignedDriver.lastName} | ${assignedDriver.phoneNumber}`,
            id: assignedDriver.id.toString(),
          },
        ]);
      } else {
        setDriverData(allDrivers || []);
      }
    } catch {
      setErrorMessage(
        "No driver is assigned to the truck towing this trailer. Please select a driver manually."
      );
      setStatus("error");
      setDriverData(allDrivers || []);
    }
  };

  const onValuesChange = (values: any) => {
    const assetAffected = values.assetAffected;
    const assetNumber = getId(values.assetNumber);
    const driverId = values.driverId;

    const assetAffectedChanged = prevAssetAffected.current !== assetAffected;
    const assetNumberChanged = prevAssetNumber.current !== assetNumber;
    const driverIdChanged = prevDriverId.current !== driverId;

    if (assetAffectedChanged || assetNumberChanged) {
      if (assetAffected === "Truck") {
        if (assetNumber) {
          setDriverData([]);
          handleFetchAssignedDriver(assetNumber);
        } else {
          setDriverData(allDrivers || []);
        }
      } else if (assetAffected === "Trailer") {
        if (assetNumber) {
          setDriverData([]);
          handleFetchDriverFromTrailer(assetNumber);
        } else {
          setDriverData(allDrivers || []);
        }
      } else {
        setDriverData(allDrivers || []);
      }
    }

    if (driverIdChanged && prevDriverId.current && !driverId) {
      setDriverData(allDrivers || []);
    }

    prevAssetAffected.current = assetAffected;
    prevAssetNumber.current = assetNumber;
    prevDriverId.current = driverId;
  };

  const dynamicFields = makeFieldConfigDynamic(
    yardRepairFieldConfiguration,
    setSelectedAssetType,
    selectedAssetType
  );

  return (
    <>
      <Button variant="contained" color="primary" onClick={handleOpen}>
        Create yard repair
      </Button>
      <GenericFormPopup
        formFields={dynamicFields}
        initialValues={yardRepairInitialValues}
        title="Yard Repair Form"
        onSubmit={submitHandler}
        open={open}
        onClose={handleClose}
        loading={yardRepairMutation.isPending}
        onValuesChange={onValuesChange}
        fieldComponents={{
          aassetNumber:
            selectedAssetType === "Trailer" ? (
              <Box display="flex" justifyContent="center" alignItems="center">
                <TrailerPopup />
              </Box>
            ) : (
              <Box display="flex" justifyContent="center" alignItems="center">
                <TruckPopup />
              </Box>
            ),
          driverId: (
            <Box display="flex" justifyContent="center" alignItems="center">
              <DriverPopup />
            </Box>
          ),
          ownerCompanyId: (
            <Box display="flex" justifyContent="center" alignItems="center">
              <CompanyPopup />
            </Box>
          ),
        }}
        autocompleteData={{
          drivers: driverData,
          assets: assetData,
          ownerCompanies,
        }}
      />
      <FileUploadModal
        open={fileUploadModalOpen}
        onClose={() => setFileUploadModalOpen(false)}
        row={yardRepairData}
        type="yardRepairInvoice"
      />
      <StatusSnackbar
        status={status}
        successMessage=" Yard repair added successfully"
        errorMessage={errorMessage}
        handleClose={handleSnackbarClose}
      />
    </>
  );
};

export default YardRepairPopup;
